/*
 * import-mapping.ts — client half of Import Hub "Use your template".
 *
 * PyAPI's column-mapping agent decides *what* each column of a customer's
 * spreadsheet is (POST /pyapi/import-hub/column-mapping); this module does
 * everything that has to touch the file itself, deterministically:
 *
 *   - buildFileProfile: a bounded JSON profile of the file (top rows of each
 *     sheet + per-column samples/distinct values). The file never leaves the
 *     browser except to the product service, exactly as before.
 *   - normalizeValue / rewriteCsvFile / buildNormalizedCsvFile: turn the
 *     user's values into what the bulk importer accepts -- YYYY-MM-DD dates
 *     (incl. Excel serial dates), plain numbers, whole-number quantities,
 *     ISO2 countries, dotted HTS codes -- so one odd cell can't fail a whole
 *     500-row import batch. Unreadable values are blanked and counted, never
 *     coerced to 0.
 *
 * Mirrors pyapi/app/models/import_mapping/models.py (profile caps) and
 * pyapi/app/services/import_mapping/fields.py (field kinds).
 */
import * as XLSX from 'xlsx';
import { countries } from 'utils/countriesWithCodes';
import type { TemplateColumn } from '../CreateImportDatabaseDialog';

/* ---------- Wire types (pyapi models.py) ---------- */
export interface ColumnProfile {
  index: number;
  samples: string[];
  distinct_values: string[] | null;
  non_empty: number;
  numeric_share: number;
}

export interface SheetProfile {
  name: string;
  row_count: number;
  top_rows: string[][];
  columns: ColumnProfile[];
}

export type ImportFileType = 'csv' | 'xlsx';

export interface ColumnMappingRequest {
  group_id: string;
  file_name: string;
  file_type: ImportFileType;
  database_structure: 'hierarchy' | 'flat';
  sheets: SheetProfile[];
}

export interface ColumnHints {
  date_order?: 'MDY' | 'DMY' | 'YMD' | null;
  decimal_separator?: '.' | ',' | null;
  /** Set on an unmapped column holding HS/HTS/Schedule B codes. */
  code_kind?: 'hs' | null;
}

export type MappingSource = 'exact' | 'heuristic' | 'ai';

export interface PlannedColumn {
  index: number;
  header: string;
  target: string | null;
  confidence: number;
  source: MappingSource;
  reason?: string | null;
  hints: ColumnHints;
  value_map?: Record<string, string> | null;
}

export interface ColumnMappingPlan {
  sheet_name: string;
  header_row_index: number;
  columns: PlannedColumn[];
  used_ai: boolean;
  model?: string | null;
  warnings: string[];
}

/* ---------- Profile caps (keep in sync with models.py) ---------- */
const MAX_SHEETS = 5;
const MAX_TOP_ROWS = 25;
// Columns the agent can map (stats are profiled for these).
const MAX_COLUMNS = 200;
// Header cells kept per top row -- wider than MAX_COLUMNS so pyapi's name
// matcher still sees a clearly named column past the cap.
const MAX_HEADER_COLUMNS = 400;
const MAX_CELL_CHARS = 120;
const MAX_SAMPLES = 10;
const MAX_DISTINCT_VALUES = 100;
// Stats come from at most this many rows of a CSV; the row count still
// covers the whole file.
const PROFILE_STATS_ROW_LIMIT = 50000;
// Serialized-profile budget, under pyapi's 400k-char ceiling.
const MAX_PROFILE_CHARS = 350000;

export const LOW_CONFIDENCE_THRESHOLD = 0.6;

/* ---------- Field kinds (fields.py) ---------- */
export type FieldKind =
  | 'text'
  | 'number'
  | 'integer'
  | 'date'
  | 'country'
  | 'hts'
  | 'parent'
  | 'enum';

export const FIELD_KINDS: Record<TemplateColumn, FieldKind> = {
  model_number: 'text',
  parent: 'parent',
  name: 'text',
  description: 'text',
  traces: 'text',
  category: 'text',
  hierarchy_type: 'enum',
  quantity: 'integer',
  unit_of_measure: 'text',
  manufacturer: 'text',
  vendor: 'text',
  specifications: 'text',
  country_of_origin: 'country',
  hts_code: 'hts',
  trans_method: 'text',
  country_of_smelt_pour: 'country',
  entry_date: 'date',
  loading_date: 'date',
  product_value: 'number',
  copper_value: 'number',
  aluminum_value: 'number',
  steel_value: 'number',
  copper_percent: 'number',
  aluminum_percent: 'number',
  steel_percent: 'number',
};

/* ---------- Streaming CSV reader ---------- */
const CSV_CHUNK_SIZE = 1024 * 1024; // 1MB

/**
 * Calls `onRow` for every non-blank CSV row, reading the file in 1MB chunks
 * so large files are never held in memory whole. Same quote-aware state
 * machine as parseCsvText / countCsvRows in CreateImportDatabaseDialog.tsx
 * (including deferring an escaped "" split across a chunk boundary), and the
 * same "blank rows don't exist" rule, so row indices agree with the dialog's
 * preview and with XLSX sheet_to_json({ blankrows: false }).
 */
export async function forEachCsvRow(
  file: Blob,
  onRow: (row: string[], index: number) => void,
  chunkSize: number = CSV_CHUNK_SIZE,
): Promise<number> {
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let carry = '';
  let index = 0;

  const commitRow = () => {
    row.push(field);
    field = '';
    if (row.some((v) => v.trim() !== '')) {
      onRow(row, index);
      index++;
    }
    row = [];
  };

  // One streaming decoder for the whole file: a multibyte UTF-8 character
  // split across a chunk boundary is held until the next chunk, instead of
  // each chunk being decoded on its own (which turned "Ö" into "��").
  const decoder = new TextDecoder('utf-8');
  for (let offset = 0; offset < file.size; offset += chunkSize) {
    const isFinalChunk = offset + chunkSize >= file.size;
    const bytes = await file.slice(offset, offset + chunkSize).arrayBuffer();
    const chunk = carry + decoder.decode(bytes, { stream: !isFinalChunk });
    carry = '';
    for (let i = 0; i < chunk.length; i++) {
      const c = chunk[i];
      if (i === chunk.length - 1 && !isFinalChunk && inQuotes && c === '"') {
        carry = '"';
        break;
      }
      if (inQuotes) {
        if (c === '"') {
          if (chunk[i + 1] === '"') {
            field += '"';
            i++;
          } else inQuotes = false;
        } else field += c;
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === ',') {
        row.push(field);
        field = '';
      } else if (c === '\r') {
        /* skip */
      } else if (c === '\n') {
        commitRow();
      } else {
        field += c;
      }
    }
  }
  if (field.length > 0 || row.length > 0) commitRow();
  return index;
}

/* ---------- XLSX sheets ---------- */
export interface WorkbookSheet {
  name: string;
  rows: string[][];
}

/** Every non-empty sheet of a workbook as string rows (blank rows dropped),
 * read the same way parseFile reads the first sheet. */
// A numeric cell with a zero-padded number format ("000000") shows its
// leading zeros but stores a plain number; keep the text the user sees.
function keepZeroPaddedText(sheet: XLSX.WorkSheet) {
  Object.keys(sheet).forEach((address) => {
    if (address.startsWith('!')) return;
    const cell = sheet[address] as XLSX.CellObject;
    if (
      cell.t === 'n' &&
      typeof cell.z === 'string' &&
      /^0+$/.test(cell.z) &&
      cell.w
    ) {
      cell.t = 's';
      cell.v = cell.w;
    }
  });
}

export async function readWorkbookSheets(file: File): Promise<WorkbookSheet[]> {
  const wb = XLSX.read(await file.arrayBuffer(), {
    type: 'array',
    cellNF: true,
  });
  wb.SheetNames.forEach((name) => keepZeroPaddedText(wb.Sheets[name]));
  return wb.SheetNames.map((name) => ({
    name,
    rows: (
      XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name], {
        header: 1,
        blankrows: false,
        defval: '',
      }) as unknown[][]
    ).map((r) => r.map((v) => String(v ?? ''))),
  })).filter((sheet) => sheet.rows.length > 0);
}

/* ---------- Profile ---------- */
const NUMERIC_RE = /^[\s$€£¥(+-]*\d[\d,.\s]*%?\)?\s*$/;

const clip = (value: string) => value.slice(0, MAX_CELL_CHARS);

class ColumnStats {
  nonEmpty = 0;

  numeric = 0;

  distinct: Set<string> | null = new Set<string>();

  values: string[] = [];

  add(raw: string) {
    const value = raw.trim();
    if (!value) return;
    this.nonEmpty++;
    if (NUMERIC_RE.test(value)) this.numeric++;
    if (this.distinct) {
      this.distinct.add(clip(value));
      if (this.distinct.size > MAX_DISTINCT_VALUES) this.distinct = null;
    }
    // Keep a bounded, evenly thinned pool to draw samples from.
    this.values.push(clip(value));
    if (this.values.length >= MAX_SAMPLES * 20) {
      this.values = this.values.filter((_, i) => i % 2 === 0);
    }
  }

  toProfile(index: number): ColumnProfile {
    const step = Math.max(1, Math.floor(this.values.length / MAX_SAMPLES));
    return {
      index,
      samples: this.values
        .filter((_, i) => i % step === 0)
        .slice(0, MAX_SAMPLES),
      distinct_values: this.distinct ? Array.from(this.distinct) : null,
      non_empty: this.nonEmpty,
      numeric_share: this.nonEmpty ? this.numeric / this.nonEmpty : 0,
    };
  }
}

class SheetProfiler {
  rowCount = 0;

  topRows: string[][] = [];

  columns: ColumnStats[] = [];

  constructor(private readonly name: string) {}

  add(row: string[]) {
    if (this.topRows.length < MAX_TOP_ROWS) {
      this.topRows.push(row.slice(0, MAX_HEADER_COLUMNS).map(clip));
    }
    if (this.rowCount < PROFILE_STATS_ROW_LIMIT) {
      row.slice(0, MAX_COLUMNS).forEach((value, i) => {
        if (!this.columns[i]) this.columns[i] = new ColumnStats();
        this.columns[i].add(value);
      });
    }
    this.rowCount++;
  }

  toProfile(): SheetProfile {
    return {
      name: this.name,
      row_count: this.rowCount,
      top_rows: this.topRows,
      columns: this.columns
        .map((stats, i) => (stats ? stats.toProfile(i) : null))
        .filter((c): c is ColumnProfile => c !== null),
    };
  }
}

async function profileCsvFile(file: File): Promise<SheetProfile> {
  const profiler = new SheetProfiler(file.name);
  await forEachCsvRow(file, (row) => profiler.add(row));
  return profiler.toProfile();
}

export function profileSheet(name: string, rows: string[][]): SheetProfile {
  const profiler = new SheetProfiler(name);
  rows.forEach((row) => profiler.add(row));
  return profiler.toProfile();
}

interface CompactionStep {
  rows: number;
  samples: number;
  distinct: number;
  cellChars: number;
  headerColumns: number;
}

// Each step shrinks the profile further: fewer preview rows and samples, then
// shorter cells, then header cells only up to the mapped-column cap.
const COMPACTION_STEPS: CompactionStep[] = [
  {
    rows: 25,
    samples: 10,
    distinct: 50,
    cellChars: MAX_CELL_CHARS,
    headerColumns: MAX_HEADER_COLUMNS,
  },
  {
    rows: 25,
    samples: 10,
    distinct: 25,
    cellChars: MAX_CELL_CHARS,
    headerColumns: MAX_HEADER_COLUMNS,
  },
  {
    rows: 10,
    samples: 10,
    distinct: 10,
    cellChars: MAX_CELL_CHARS,
    headerColumns: MAX_HEADER_COLUMNS,
  },
  {
    rows: 10,
    samples: 5,
    distinct: 5,
    cellChars: MAX_CELL_CHARS,
    headerColumns: MAX_HEADER_COLUMNS,
  },
  {
    rows: 10,
    samples: 5,
    distinct: 5,
    cellChars: 60,
    headerColumns: MAX_HEADER_COLUMNS,
  },
  {
    rows: 8,
    samples: 3,
    distinct: 3,
    cellChars: 30,
    headerColumns: MAX_HEADER_COLUMNS,
  },
  {
    rows: 8,
    samples: 2,
    distinct: 0,
    cellChars: 30,
    headerColumns: MAX_COLUMNS,
  },
];

function compactProfile(
  request: ColumnMappingRequest,
  step: CompactionStep,
): ColumnMappingRequest {
  const cut = (value: string) => value.slice(0, step.cellChars);
  return {
    ...request,
    sheets: request.sheets.map((sheet) => ({
      ...sheet,
      top_rows: sheet.top_rows
        .slice(0, step.rows)
        .map((row) => row.slice(0, step.headerColumns).map(cut)),
      columns: sheet.columns.map((column) => ({
        ...column,
        samples: column.samples.slice(0, step.samples).map(cut),
        // A cut-down list would read as the column's complete set of
        // values (pyapi checks parent links and empty columns against it),
        // so a list that no longer fits is sent as "too many to list".
        distinct_values:
          column.distinct_values &&
          column.distinct_values.length <= step.distinct
            ? column.distinct_values.map(cut)
            : null,
      })),
    })),
  };
}

const profileFits = (request: ColumnMappingRequest) =>
  JSON.stringify(request).length <= MAX_PROFILE_CHARS;

/** Compacts the profile until it fits pyapi's request ceiling, and fails
 * locally if it can't -- the dialog then keeps exact-name matching -- rather
 * than sending a request pyapi would reject. */
export function fitProfile(
  request: ColumnMappingRequest,
): ColumnMappingRequest {
  if (profileFits(request)) return request;
  for (const step of COMPACTION_STEPS) {
    const compacted = compactProfile(request, step);
    if (profileFits(compacted)) return compacted;
  }
  throw new Error('This file is too large for automatic column matching.');
}

export async function buildFileProfile(
  file: File,
  fileType: ImportFileType,
  context: {
    groupId: string;
    databaseStructure: 'hierarchy' | 'flat';
    workbookSheets?: WorkbookSheet[];
  },
): Promise<ColumnMappingRequest> {
  const sheets =
    fileType === 'xlsx'
      ? (context.workbookSheets ?? (await readWorkbookSheets(file)))
          .slice(0, MAX_SHEETS)
          .map((sheet) => profileSheet(sheet.name, sheet.rows))
      : [await profileCsvFile(file)];
  return fitProfile({
    group_id: context.groupId,
    file_name: file.name,
    file_type: fileType,
    database_structure: context.databaseStructure,
    sheets,
  });
}

/* ---------- Value normalization ---------- */
export type NormalizeStatus =
  | 'ok'
  | 'blanked'
  | 'rounded'
  | 'unresolved'
  | 'trimmed';

export interface NormalizeContext {
  fileType: ImportFileType;
  hints?: ColumnHints;
  valueMap?: Record<string, string> | null;
}

export interface NormalizedValue {
  value: string;
  status: NormalizeStatus;
}

const MONTHS: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const pad2 = (n: number) => String(n).padStart(2, '0');

function isoDate(year: number, month: number, day: number): string | null {
  const fullYear = year < 100 ? (year < 70 ? 2000 + year : 1900 + year) : year;
  if (fullYear < 1900 || fullYear > 2200) return null;
  const date = new Date(Date.UTC(fullYear, month - 1, day));
  if (
    date.getUTCFullYear() !== fullYear ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${fullYear}-${pad2(month)}-${pad2(day)}`;
}

// Excel stores dates as days since 1899-12-30 (the 1900 leap-year bug is
// baked into that epoch).
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const DAY_MS = 24 * 60 * 60 * 1000;

function normalizeDate(raw: string, ctx: NormalizeContext): string | null {
  const text = raw.trim();
  if (ctx.fileType === 'xlsx' && /^\d+(\.\d+)?$/.test(text)) {
    const serial = Number(text);
    if (serial >= 20000 && serial <= 80000) {
      const date = new Date(EXCEL_EPOCH_MS + Math.floor(serial) * DAY_MS);
      return isoDate(
        date.getUTCFullYear(),
        date.getUTCMonth() + 1,
        date.getUTCDate(),
      );
    }
  }

  let m = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:$|[ T])/);
  if (m) return isoDate(Number(m[1]), Number(m[2]), Number(m[3]));

  m = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})(?:$|[ T])/);
  if (m) {
    const first = Number(m[1]);
    const second = Number(m[2]);
    let order = ctx.hints?.date_order || 'MDY';
    if (first > 12) order = 'DMY';
    else if (second > 12) order = 'MDY';
    return order === 'DMY'
      ? isoDate(Number(m[3]), second, first)
      : isoDate(Number(m[3]), first, second);
  }

  // 15-Jan-2026, 15 January 2026
  m = text.match(/^(\d{1,2})[\s-]+([A-Za-z]{3,})\.?[\s,-]+(\d{2}|\d{4})$/);
  if (m) {
    const month = MONTHS[m[2].slice(0, 3).toLowerCase()];
    return month ? isoDate(Number(m[3]), month, Number(m[1])) : null;
  }
  // Jan 15, 2026
  m = text.match(/^([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{4})$/);
  if (m) {
    const month = MONTHS[m[1].slice(0, 3).toLowerCase()];
    return month ? isoDate(Number(m[3]), month, Number(m[2])) : null;
  }
  return null;
}

// One number, optionally digit-grouped with "," "." or spaces, with an
// optional decimal part.
const NUMBER_TOKEN_RE =
  /^(?:(?:\d{1,3}(?:[.,\s]\d{3})+|\d+)(?:[.,]\d+)?|[.,]\d+)$/;

function normalizeNumber(raw: string, ctx: NormalizeContext): number | null {
  let text = raw.trim();
  let negative = false;
  // Only supported decorations are removed -- accounting parentheses, a sign,
  // a leading currency code/symbol, and a trailing %, unit, currency code or
  // symbol. What's left must be a single number: "1-2", "12abc34", "1e3" or
  // "10-20" are blanked rather than turned into a different number.
  const parens = text.match(/^\((.*)\)$/);
  if (parens) {
    negative = true;
    text = parens[1].trim();
  }
  const leading = text.match(/^([+-])?\s*(?:[A-Z]{3}\s*)?[$€£¥]?\s*([+-])?\s*/);
  if (leading && (leading[1] === '-' || leading[2] === '-')) negative = true;
  text = text.slice(leading ? leading[0].length : 0);
  text = text.replace(/\s*(?:%|[$€£¥]|[A-Za-z]{1,12}\.?)$/, '');
  if (!NUMBER_TOKEN_RE.test(text)) return null;
  if (ctx.hints?.decimal_separator === ',') {
    text = text.replace(/[.\s]/g, '').replace(',', '.');
  } else {
    text = text.replace(/[,\s]/g, '');
  }
  if ((text.match(/\./g) || []).length > 1) return null;
  const value = Number(text);
  if (!Number.isFinite(value)) return null;
  return negative ? -value : value;
}

function htsDigits(text: string): string {
  // A code that went through a numeric spreadsheet cell (even one later
  // saved as CSV) loses its formatting: 0101.21 -> 101.21 and 8471.30 ->
  // 8471.3. Restore the 4-digit heading and pair up the decimals.
  const excelNumber = text.match(/^(\d{1,4})\.(\d+)$/);
  if (excelNumber) {
    const decimals = excelNumber[2];
    return (
      excelNumber[1].padStart(4, '0') +
      decimals.padEnd(decimals.length + (decimals.length % 2), '0')
    );
  }
  const digits = text.replace(/\D/g, '');
  // A heading in chapters 01-09 that lost its leading zero.
  return [5, 7, 9].includes(digits.length) ? `0${digits}` : digits;
}

const isHtsLength = (digits: string) => [4, 6, 8, 10].includes(digits.length);

const formatHtsDigits = (digits: string) =>
  [
    digits.slice(0, 4),
    digits.slice(4, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
  ]
    .filter(Boolean)
    .join('.');

// Chapter 98/99 codes are special-program and trade-remedy provisions
// (Section 301/232, IEEPA, ...) filed alongside a product's real
// classification, never the classification itself.
const isProgramCode = (digits: string) => /^9[89]/.test(digits);

function normalizeHts(raw: string): NormalizedValue {
  const text = raw.trim();
  // No digits at all is a placeholder (TBD, N/A, pending), not a code.
  if (!/\d/.test(text)) return { value: '', status: 'blanked' };

  // Several codes in one cell, e.g. "9903.88.03 + 8541.43.0010": keep the
  // merchandise classification and drop the program codes riding with it.
  const parts = text
    .split(/\s*(?:[+;,/&|]|\band\b)\s*/i)
    .filter((part) => part.trim());
  if (parts.length > 1) {
    const codes = parts.map(htsDigits).filter(isHtsLength);
    const merchandise = codes.find((digits) => !isProgramCode(digits));
    if (merchandise) {
      return {
        value: formatHtsDigits(merchandise),
        status: codes.length > 1 ? 'trimmed' : 'ok',
      };
    }
  }

  const digits = htsDigits(text);
  return isHtsLength(digits)
    ? { value: formatHtsDigits(digits), status: 'ok' }
    : { value: text, status: 'ok' };
}

/**
 * Zeros a numeric spreadsheet cell dropped from a code in an *unmapped*
 * HS/Schedule B column (030211 -> 30211, 8471.30 -> 8471.3). Anything else
 * is left exactly as the user wrote it -- this is their own extra column.
 */
export function restoreCodeZeros(raw: string): string {
  const text = raw.trim();
  if (/^(?:\d{5}|\d{7}|\d{9})$/.test(text)) return `0${text}`;
  if (/^\d{1,4}\.\d+$/.test(text)) {
    const digits = htsDigits(text);
    if (isHtsLength(digits)) return formatHtsDigits(digits);
  }
  return raw;
}

const COUNTRY_BY_KEY = new Map<string, string>();
countries.forEach(({ name, iso2 }) => {
  if (iso2 === 'UN') return;
  COUNTRY_BY_KEY.set(name.toLowerCase(), iso2);
  COUNTRY_BY_KEY.set(iso2.toLowerCase(), iso2);
});

function normalizeCountry(raw: string, ctx: NormalizeContext): string | null {
  const text = raw.trim();
  const mapped = ctx.valueMap?.[text] ?? ctx.valueMap?.[raw];
  if (mapped) return mapped;
  return COUNTRY_BY_KEY.get(text.toLowerCase()) ?? null;
}

export function normalizeValue(
  kind: FieldKind,
  raw: string,
  ctx: NormalizeContext,
): NormalizedValue {
  const text = (raw ?? '').trim();
  if (!text) return { value: '', status: 'ok' };
  switch (kind) {
    case 'date': {
      const value = normalizeDate(text, ctx);
      return value ? { value, status: 'ok' } : { value: '', status: 'blanked' };
    }
    case 'number': {
      const value = normalizeNumber(text, ctx);
      return value === null
        ? { value: '', status: 'blanked' }
        : { value: String(value), status: 'ok' };
    }
    case 'integer': {
      const value = normalizeNumber(text, ctx);
      if (value === null) return { value: '', status: 'blanked' };
      const rounded = Math.round(value);
      return {
        value: String(rounded),
        status: rounded === value ? 'ok' : 'rounded',
      };
    }
    case 'hts':
      return normalizeHts(text);
    case 'country': {
      const value = normalizeCountry(text, ctx);
      // The product service stores an unrecognized country verbatim, so the
      // raw text is kept rather than lost.
      return value
        ? { value, status: 'ok' }
        : { value: text, status: 'unresolved' };
    }
    default:
      return { value: text, status: 'ok' };
  }
}

/* ---------- Normalized CSV ---------- */
export interface ColumnNormalization {
  hints?: ColumnHints;
  valueMap?: Record<string, string> | null;
}

export interface NormalizeFileOptions {
  fileType: ImportFileType;
  headerRowIndex: number;
  /** Header row to write in place of the file's own (e.g. padded to the full
   * column width, so every resolvedHeaders position has a column). */
  headers?: string[];
  mapping: Record<number, TemplateColumn | ''>;
  columns: Record<number, ColumnNormalization>;
}

export interface NormalizationStats {
  blankedDates: number;
  blankedNumbers: number;
  blankedHtsCodes: number;
  trimmedProgramCodes: number;
  roundedQuantities: number;
  unresolvedCountries: number;
  restoredCodes: number;
  droppedRows: number;
}

export const emptyNormalizationStats = (): NormalizationStats => ({
  blankedDates: 0,
  blankedNumbers: 0,
  blankedHtsCodes: 0,
  trimmedProgramCodes: 0,
  roundedQuantities: 0,
  unresolvedCountries: 0,
  restoredCodes: 0,
  droppedRows: 0,
});

const IDENTITY_FIELDS: TemplateColumn[] = [
  'name',
  'description',
  'model_number',
];

export function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function makeRowNormalizer(
  options: NormalizeFileOptions,
  stats: NormalizationStats,
) {
  const mapped = Object.entries(options.mapping)
    .filter(([, field]) => !!field)
    .map(([index, field]) => ({
      index: Number(index),
      kind: FIELD_KINDS[field as TemplateColumn],
      ctx: {
        fileType: options.fileType,
        hints: options.columns[Number(index)]?.hints,
        valueMap: options.columns[Number(index)]?.valueMap,
      } as NormalizeContext,
    }));
  const identity = Object.entries(options.mapping)
    .filter(([, field]) => IDENTITY_FIELDS.includes(field as TemplateColumn))
    .map(([index]) => Number(index));
  // Unmapped code columns (they land in additional_columns) only get their
  // dropped zeros back; a column the user mapped is cleaned by its field.
  const codeColumns = Object.entries(options.columns)
    .filter(
      ([index, column]) =>
        column.hints?.code_kind === 'hs' && !options.mapping[Number(index)],
    )
    .map(([index]) => Number(index));

  return (row: string[]): string[] | null => {
    // Same rule as buildMappedProductsCsv and the product worker: a row
    // with no name, description or model number can't be imported.
    if (!identity.some((index) => (row[index] ?? '').trim())) {
      stats.droppedRows++;
      return null;
    }
    const out = row.slice();
    mapped.forEach(({ index, kind, ctx }) => {
      if (index >= out.length) return;
      const { value, status } = normalizeValue(kind, out[index], ctx);
      out[index] = value;
      if (status === 'blanked') {
        if (kind === 'date') stats.blankedDates++;
        else if (kind === 'hts') stats.blankedHtsCodes++;
        else stats.blankedNumbers++;
      } else if (status === 'trimmed') {
        stats.trimmedProgramCodes++;
      } else if (status === 'rounded') {
        stats.roundedQuantities++;
      } else if (status === 'unresolved') {
        stats.unresolvedCountries++;
      }
    });
    codeColumns.forEach((index) => {
      if (index >= out.length) return;
      const restored = restoreCodeZeros(out[index]);
      if (restored !== out[index]) {
        out[index] = restored;
        stats.restoredCodes++;
      }
    });
    return out;
  };
}

const normalizedFileName = (name: string) =>
  `${name.replace(/\.[^.]+$/, '') || 'products'}.csv`;

/**
 * Normalized CSV from in-memory rows (an XLSX sheet). `rows` are the sheet's
 * rows from the top; everything above `headerRowIndex` is dropped, the header
 * row is written unchanged, and data rows are normalized per the mapping.
 */
export function buildNormalizedCsvFile(
  rows: string[][],
  fileName: string,
  options: NormalizeFileOptions,
): { file: File; stats: NormalizationStats } {
  const stats = emptyNormalizationStats();
  const normalizeRow = makeRowNormalizer(options, stats);
  const lines: string[] = [];
  rows.forEach((row, index) => {
    if (index < options.headerRowIndex) return;
    const out =
      index === options.headerRowIndex
        ? options.headers ?? row
        : normalizeRow(row);
    if (out) lines.push(out.map(csvCell).join(','));
  });
  return {
    file: new File([lines.join('\n')], normalizedFileName(fileName), {
      type: 'text/csv',
    }),
    stats,
  };
}

/**
 * Streaming equivalent of buildNormalizedCsvFile for a CSV upload: reads the
 * file in chunks and emits Blob parts per batch of rows, so the rewritten
 * file is never one giant string in memory.
 */
export async function rewriteCsvFile(
  file: File,
  options: NormalizeFileOptions,
): Promise<{ file: File; stats: NormalizationStats }> {
  const stats = emptyNormalizationStats();
  const normalizeRow = makeRowNormalizer(options, stats);
  const parts: string[] = [];
  let batch: string[] = [];
  const flush = () => {
    if (batch.length) parts.push(`${batch.join('\n')}\n`);
    batch = [];
  };
  await forEachCsvRow(file, (row, index) => {
    if (index < options.headerRowIndex) return;
    const out =
      index === options.headerRowIndex
        ? options.headers ?? row
        : normalizeRow(row);
    if (!out) return;
    batch.push(out.map(csvCell).join(','));
    if (batch.length >= 1000) flush();
  });
  flush();
  return {
    file: new File(parts, normalizedFileName(file.name), { type: 'text/csv' }),
    stats,
  };
}

/**
 * Short warning for the import toast, only when cleanup actually lost data:
 * values that couldn't be read and were left blank. Pure formatting fixes
 * (restored zeros, reformatted codes, dates and countries) stay silent.
 */
export function describeNormalization(
  stats: NormalizationStats,
): string | null {
  const blanked =
    stats.blankedDates + stats.blankedNumbers + stats.blankedHtsCodes;
  if (!blanked) return null;
  return `${blanked} value${blanked === 1 ? '' : 's'} couldn't be read and ${
    blanked === 1 ? 'was' : 'were'
  } left blank.`;
}
